import { useEffect, useMemo, useState } from 'react';

export const BONUS_COUNTDOWN_PLACEHOLDER = '--:--:--';

function secondsUntil(deadline: number | null) {
	if (deadline === null) return 0;
	return Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
}

function formatDuration(totalSeconds: number) {
	const s = Math.max(0, totalSeconds);
	const h = String(Math.floor(s / 3600)).padStart(2, '0');
	const m = String(Math.floor((s % 3600) / 60)).padStart(2, '0');
	const sec = String(Math.floor(s % 60)).padStart(2, '0');
	return `${h}:${m}:${sec}`;
}

export function useBonusCountdown(collectedAt?: string, bonusHours?: number) {
	// null means "cooldown not known yet" (profile still loading). Callers must render
	// the placeholder instead of a guessed duration - guessing 24h made the button flash
	// 24:00:00 on app start until the profile request resolved.
	const deadline = useMemo(() => {
		if (!collectedAt || bonusHours == null || Number.isNaN(bonusHours) || bonusHours < 0) {
			return null;
		}

		const collected = Date.parse(collectedAt);
		if (Number.isNaN(collected)) return null;

		return collected + bonusHours * 3600 * 1000;
	}, [collectedAt, bonusHours]);

	const [remaining, setRemaining] = useState(() => secondsUntil(deadline));

	useEffect(() => {
		setRemaining(secondsUntil(deadline));
		if (deadline === null || deadline <= Date.now()) return;

		// Recomputed from the deadline on every tick rather than decremented, so the
		// countdown stays correct after the app was backgrounded and timers were throttled.
		const id = setInterval(() => {
			const next = secondsUntil(deadline);
			setRemaining(next);
			if (next <= 0) clearInterval(id);
		}, 1000);

		return () => clearInterval(id);
	}, [deadline]);

	const isKnown = deadline !== null;

	return {
		isKnown,
		remainingSeconds: remaining,
		timeText: isKnown ? formatDuration(remaining) : BONUS_COUNTDOWN_PLACEHOLDER,
		ready: isKnown && remaining <= 0,
	};
}
